import React, { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { Plus, Pencil, Trash2, Info, CheckCircle2, AlertTriangle, ShieldAlert } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";

const TYPE_META = {
  info:    { label: "Information", icon: Info, color: "text-sky-600 bg-sky-50 border-sky-200 dark:bg-sky-950/40 dark:border-sky-900" },
  success: { label: "Succès",      icon: CheckCircle2, color: "text-emerald-600 bg-emerald-50 border-emerald-200 dark:bg-emerald-950/40 dark:border-emerald-900" },
  warning: { label: "Avertissement", icon: AlertTriangle, color: "text-amber-600 bg-amber-50 border-amber-200 dark:bg-amber-950/40 dark:border-amber-900" },
  danger:  { label: "Alerte",      icon: ShieldAlert, color: "text-rose-600 bg-rose-50 border-rose-200 dark:bg-rose-950/40 dark:border-rose-900" },
};

const empty = { id: null, message: "", type: "info", link_url: "", enabled: true, starts_at: "", ends_at: "" };

export default function AnnouncementsManager() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState(empty);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/announcements");
      setItems(data);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.message.trim()) return toast.error("Le message est obligatoire.");
    const payload = {
      message: form.message.trim(),
      type: form.type,
      link_url: form.link_url.trim() || null,
      enabled: form.enabled,
      starts_at: form.starts_at || null,
      ends_at: form.ends_at || null,
    };
    try {
      if (form.id) {
        await api.patch(`/admin/announcements/${form.id}`, payload);
        toast.success("Annonce mise à jour");
      } else {
        await api.post("/admin/announcements", payload);
        toast.success("Annonce créée");
      }
      setForm(empty);
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    }
  };

  const edit = (a) => setForm({
    id: a.id, message: a.message, type: a.type, link_url: a.link_url || "",
    enabled: a.enabled, starts_at: a.starts_at || "", ends_at: a.ends_at || ""
  });

  const remove = async (a) => {
    if (!window.confirm("Supprimer cette annonce ?")) return;
    try {
      await api.delete(`/admin/announcements/${a.id}`);
      toast.success("Annonce supprimée");
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || e.message); }
  };

  const toggleEnabled = async (a) => {
    try {
      await api.patch(`/admin/announcements/${a.id}`, { enabled: !a.enabled });
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || e.message); }
  };

  const deleteDisabled = async () => {
    const n = items.filter((a) => !a.enabled).length;
    if (n === 0) return;
    if (!window.confirm(`Supprimer définitivement ${n} annonce(s) désactivée(s) ?`)) return;
    try {
      const { data } = await api.post("/admin/announcements/delete-disabled");
      toast.success(`${data.deleted} annonce(s) supprimée(s).`);
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || e.message); }
  };

  const disabledCount = items.filter((a) => !a.enabled).length;

  return (
    <div className="space-y-6" data-testid="admin-announcements-page">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <p className="text-xs uppercase tracking-[0.2em] text-muted-foreground mb-2">// Communication</p>
          <h1 className="text-3xl sm:text-4xl font-black tracking-tight">Annonces</h1>
          <p className="text-sm text-muted-foreground mt-2 max-w-2xl">
            Messages affichés au-dessus des icônes sur la page d&apos;accueil. Plusieurs annonces actives sont mises en rotation automatique (toutes les 8s).
          </p>
        </div>
        {disabledCount > 0 && (
          <Button type="button" variant="destructive" size="sm" onClick={deleteDisabled} data-testid="ann-delete-all-disabled">
            <Trash2 className="h-4 w-4 mr-2" /> Supprimer les {disabledCount} désactivée{disabledCount > 1 ? "s" : ""}
          </Button>
        )}
      </div>

      <div className="grid md:grid-cols-2 gap-6">
        {/* Form */}
        <form onSubmit={submit} className="border border-border bg-card rounded-sm p-6 space-y-4" data-testid="announcement-form">
          <h2 className="font-bold">{form.id ? "Modifier l'annonce" : "Nouvelle annonce"}</h2>
          <div>
            <Label className="text-xs uppercase tracking-wider">Message *</Label>
            <Textarea
              rows={3}
              required
              maxLength={500}
              value={form.message}
              onChange={(e) => setForm((f) => ({ ...f, message: e.target.value }))}
              className="mt-2"
              placeholder="Ex : Maintenance prévue dimanche 22h-23h."
              data-testid="ann-form-message"
            />
          </div>
          <div>
            <Label className="text-xs uppercase tracking-wider">Type</Label>
            <Select value={form.type} onValueChange={(v) => setForm((f) => ({ ...f, type: v }))}>
              <SelectTrigger className="mt-2" data-testid="ann-form-type"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="info">Information (bleu)</SelectItem>
                <SelectItem value="success">Succès (vert)</SelectItem>
                <SelectItem value="warning">Avertissement (jaune)</SelectItem>
                <SelectItem value="danger">Alerte (rouge)</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs uppercase tracking-wider">Lien associé (optionnel)</Label>
            <Input
              type="url"
              value={form.link_url}
              onChange={(e) => setForm((f) => ({ ...f, link_url: e.target.value }))}
              className="mt-2"
              placeholder="https://…"
              data-testid="ann-form-url"
            />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs uppercase tracking-wider">Début (optionnel)</Label>
              <Input
                type="datetime-local"
                value={form.starts_at ? form.starts_at.slice(0, 16) : ""}
                onChange={(e) => setForm((f) => ({ ...f, starts_at: e.target.value }))}
                className="mt-2"
              />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider">Fin (optionnel)</Label>
              <Input
                type="datetime-local"
                value={form.ends_at ? form.ends_at.slice(0, 16) : ""}
                onChange={(e) => setForm((f) => ({ ...f, ends_at: e.target.value }))}
                className="mt-2"
              />
            </div>
          </div>
          <div className="flex items-center gap-3">
            <Switch
              checked={form.enabled}
              onCheckedChange={(v) => setForm((f) => ({ ...f, enabled: v }))}
              data-testid="ann-form-enabled"
            />
            <span className="text-sm">Annonce activée</span>
          </div>
          <div className="flex justify-end gap-2 pt-2">
            {form.id && (
              <button type="button" onClick={() => setForm(empty)} className="h-10 px-4 rounded-sm border border-border text-sm font-medium hover:bg-accent">
                Annuler
              </button>
            )}
            <button
              type="submit"
              className="h-10 px-4 rounded-sm bg-primary text-primary-foreground hover:bg-primary/90 text-sm font-medium inline-flex items-center gap-2"
              data-testid="ann-form-submit"
            >
              <Plus className="h-4 w-4" />
              {form.id ? "Enregistrer" : "Créer l'annonce"}
            </button>
          </div>
        </form>

        {/* List */}
        <div className="border border-border bg-card rounded-sm p-4">
          <h2 className="font-bold mb-4 px-2">Annonces existantes ({items.length})</h2>
          {loading ? (
            <p className="p-4 text-sm text-muted-foreground">Chargement…</p>
          ) : items.length === 0 ? (
            <p className="p-8 text-sm text-muted-foreground text-center" data-testid="ann-empty">
              Aucune annonce. Créez la première à gauche.
            </p>
          ) : (
            <ul className="space-y-2">
              {items.map((a) => {
                const meta = TYPE_META[a.type] || TYPE_META.info;
                const Icon = meta.icon;
                return (
                  <li
                    key={a.id}
                    className={`relative pl-12 pr-3 py-3 rounded-md border ${meta.color}`}
                    data-testid={`ann-row-${a.id}`}
                  >
                    <Icon className="h-5 w-5 absolute left-3 top-3.5" />
                    <div className="flex items-start justify-between gap-3">
                      <div className="min-w-0 flex-1">
                        <p className="text-sm whitespace-pre-wrap break-words">{a.message}</p>
                        <p className="text-[11px] text-muted-foreground mt-1">
                          {a.enabled ? "✓ Activée" : "○ Désactivée"}
                          {a.starts_at && ` • dès ${new Date(a.starts_at).toLocaleString("fr-FR")}`}
                          {a.ends_at && ` • jusqu'au ${new Date(a.ends_at).toLocaleString("fr-FR")}`}
                        </p>
                      </div>
                      <div className="flex gap-1 shrink-0">
                        <button
                          onClick={() => toggleEnabled(a)}
                          className="h-8 w-8 inline-flex items-center justify-center rounded-sm border border-border hover:bg-accent text-foreground"
                          title={a.enabled ? "Désactiver" : "Activer"}
                          data-testid={`ann-toggle-${a.id}`}
                        >
                          <i className={`fa-solid ${a.enabled ? "fa-toggle-on" : "fa-toggle-off"}`} />
                        </button>
                        <button
                          onClick={() => edit(a)}
                          className="h-8 w-8 inline-flex items-center justify-center rounded-sm border border-border hover:bg-accent text-foreground"
                          data-testid={`ann-edit-${a.id}`}
                        >
                          <Pencil className="h-3.5 w-3.5" />
                        </button>
                        <button
                          onClick={() => remove(a)}
                          className="h-8 w-8 inline-flex items-center justify-center rounded-sm border border-border hover:bg-destructive hover:text-destructive-foreground hover:border-destructive text-foreground"
                          data-testid={`ann-delete-${a.id}`}
                        >
                          <Trash2 className="h-3.5 w-3.5" />
                        </button>
                      </div>
                    </div>
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      </div>
    </div>
  );
}
